import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { subDays, format as formatDate } from 'date-fns';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import EmbeddedApp from 'garaje/components/embedded-app';
import UiClassicModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type FlowModel from 'garaje/models/flow';
import type CurrentLocationService from 'garaje/services/current-location';
import type MessageBusService from 'garaje/services/message-bus';
import type StateService from 'garaje/services/state';
import { DEFAULT_FLOW_NAME, FlowType } from 'garaje/utils/enums';
import urlBuilder from 'garaje/utils/url-builder';

interface AnalyticsExportModalMessage {
  event: 'showModal' | 'closeModal';
  exportType?: 'entries' | 'invites';
  query?: {
    flow_id?: string;
    start?: string;
    end?: string;
  };
  label?: string;
}

interface AnalyticsExportModalSignature {
  Element: HTMLDivElement;
  Args: {
    // For log pages (pass @isOpen to control externally)
    isOpen?: boolean;
    onClose?: () => void;
    scope?: 'location' | 'global';
    exportType?: 'entries' | 'invites';
    selectedFlow?: string;
    flows?: FlowModel[];
    locationFlowIds?: string[];
    walkinFlowId?: string;
    // Optional: directly provide flowId, label, or locationId (overrides computed values)
    flowId?: string;
    label?: string;
    locationId?: string;
    // Optional: override default date range (defaults to last 30 days)
    startDate?: string;
    endDate?: string;

    // For analytics dashboards: don't pass @isOpen - component listens to messages
  };
}

/**
 * A component that renders an export modal for analytics data with improved export URLs.
 *
 * It encapsulates the logic for building improved export URLs and rendering the embedded app
 * within a modal, reducing duplication across multiple controllers and templates by computing
 * company IDs, location IDs, flow IDs, labels, and dates internally.
 *
 * @example
 * ```hbs
 * <Analytics::ExportModal
 *   @isOpen={{this.showExportModal}}
 *   @onClose={{this.toggleModal}}
 *   @scope="location"
 *   @exportType="invites"
 *   @selectedFlow={{this.selectedFlow}}
 *   @flows={{this.model.flows}}
 *   @locationFlowIds={{this.model.locationFlowIds}}
 * />
 * ```
 */
export default class AnalyticsExportModal extends Component<AnalyticsExportModalSignature> {
  @service declare state: StateService;
  @service declare currentLocation: CurrentLocationService;
  @service declare messageBus: MessageBusService;

  // For message mode
  @tracked isModalOpen = false;
  @tracked _exportType: 'entries' | 'invites' = 'entries';
  @tracked _flowId = '';
  @tracked _startDate = '';
  @tracked _endDate = '';
  @tracked _label = '';

  constructor(owner: unknown, args: AnalyticsExportModalSignature['Args']) {
    super(owner, args);
    // Always listen to messages (for closeModal from export iframe)
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.on('embedded-app-message', this, this.handleMessage);
  }

  willDestroy(): void {
    super.willDestroy();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.off('embedded-app-message', this, this.handleMessage);
  }

  @action
  handleMessage(message: AnalyticsExportModalMessage): void {
    if (message.event === 'showModal') {
      // Handle standardized showModal event
      // For dashboard pages, exportType comes from message
      // For log pages, @exportType is passed as prop
      if (message.exportType) {
        this._exportType = message.exportType;
      }
      this._flowId = message.query?.flow_id || '';
      this._startDate = message.query?.start || '';
      this._endDate = message.query?.end || '';
      this._label = message.label || '';
      this.isModalOpen = true;
    } else if (message.event === 'closeModal') {
      // Handle close event (from export iframe inside modal)
      this.closeModal();
    }
  }

  @action
  closeModal(): void {
    this.isModalOpen = false;
    this.args.onClose?.();
  }

  get isInMessageMode(): boolean {
    // If @isOpen is not provided, we're in message mode (analytics dashboards)
    return this.args.isOpen === undefined;
  }

  get isOpen(): boolean {
    // If @isOpen is provided, use it (log pages)
    // Otherwise use internal state (analytics dashboards listening to messages)
    return this.isInMessageMode ? this.isModalOpen : this.args.isOpen ?? false;
  }

  get exportType(): 'entries' | 'invites' {
    // If @exportType is provided (log pages), use it
    // Otherwise use _exportType (dashboard pages in message mode)
    return this.args.exportType ?? this._exportType;
  }

  get companyId(): string {
    return this.state.currentCompany.id;
  }

  get locationId(): string | undefined {
    // If locationId is provided directly, use it (for multi-location exports)
    if (this.args.locationId !== undefined) {
      return this.args.locationId;
    }

    return this.args.scope === 'location' ? this.currentLocation.location.id : undefined;
  }

  get flowId(): string | undefined {
    // If in message mode, use message data
    if (this.isInMessageMode) {
      return this._flowId;
    }

    // If flowId is provided directly, use it
    if (this.args.flowId !== undefined) {
      return this.args.flowId;
    }

    const { selectedFlow, flows, locationFlowIds, walkinFlowId } = this.args;

    // If no flow is selected, return all location flow IDs
    if (!selectedFlow || selectedFlow === '') {
      if (locationFlowIds) {
        const ids = [...locationFlowIds];
        // Add walkin flow ID if provided
        if (walkinFlowId) {
          ids.push(walkinFlowId);
        }
        return ids.join(',');
      }
      // Fallback: if flows are provided, return all flow IDs
      if (flows) {
        return flows.map((flow) => flow.id).join(',');
      }
      return undefined;
    }

    if (!flows) return undefined;

    if (selectedFlow === DEFAULT_FLOW_NAME.EMPLOYEE_SCREENING) {
      return flows.find((flow) => flow.type === FlowType.EMPLOYEE_SCREENING)?.id;
    }

    return flows.find((flow) => flow.name === selectedFlow)?.id;
  }

  get label(): string {
    // If in message mode, use message data
    if (this.isInMessageMode) {
      return this._label;
    }

    // If label is provided directly, use it
    if (this.args.label) {
      return this.args.label;
    }

    const { selectedFlow } = this.args;
    return !selectedFlow || selectedFlow === '' ? 'All employees types' : selectedFlow;
  }

  get startDate(): string {
    // If in message mode, use message data
    if (this.isInMessageMode && this._startDate) {
      return this._startDate;
    }

    // Use provided startDate or default to 30 days ago
    if (this.args.startDate) return this.args.startDate;
    return formatDate(subDays(new Date(), 30), 'yyyy-MM-dd');
  }

  get endDate(): string {
    // If in message mode, use message data
    if (this.isInMessageMode && this._endDate) {
      return this._endDate;
    }

    // Use provided endDate or default to today
    if (this.args.endDate) return this.args.endDate;
    return formatDate(new Date(), 'yyyy-MM-dd');
  }

  get exportUrl(): string {
    const scope = this.args.scope ?? 'location';
    const exportType = this.exportType;
    const companyId = this.companyId;
    const locationId = this.locationId;
    const flowId = this.flowId;
    const startDate = this.startDate;
    const endDate = this.endDate;
    const label = this.label;
    const loginRequired = true;

    // Build improved export URLs based on scope and export type
    if (scope === 'location' && exportType === 'entries') {
      return urlBuilder.improvedEntryExportModalLocationUrl(
        companyId,
        locationId!,
        flowId,
        startDate,
        endDate,
        label,
        loginRequired,
      );
    } else if (scope === 'location' && exportType === 'invites') {
      return urlBuilder.improvedInviteExportModalLocationUrl(
        companyId,
        locationId!,
        flowId,
        startDate,
        endDate,
        label,
        loginRequired,
      );
    } else if (scope === 'global' && exportType === 'entries') {
      return urlBuilder.improvedEntryExportModalGlobalUrl(companyId, flowId, startDate, endDate, label, loginRequired);
    } else if (scope === 'global' && exportType === 'invites') {
      return urlBuilder.improvedInviteExportModalGlobalUrl(companyId, flowId, startDate, endDate, label, loginRequired);
    }

    // Fallback (should never reach here)
    return '';
  }

  get modalId(): string {
    const scope = this.args.scope ?? 'location';
    const exportType = this.exportType;
    return `${scope}-${exportType}-export`;
  }

  <template>
    {{#if this.isOpen}}
      <UiClassicModalDialog
        @clickOutsideToClose={{true}}
        @onClose={{this.closeModal}}
        @translucentOverlay={{true}}
        @containerClass="!max-w-export"
      >
        <FocusTrapper>
          <EmbeddedApp @id={{this.modalId}} @url={{this.exportUrl}} class="-mx-8" data-test-export-iframe />
        </FocusTrapper>
      </UiClassicModalDialog>
    {{/if}}
  </template>
}
